import type { CatalogItemBadgeInput } from "./catalog-item-badges";
import type { CatalogItemImageInput } from "./catalog-item-image";

export type { CatalogEiTier } from "./catalog-item-badges";
export type { CatalogItemImageInput } from "./catalog-item-image";

// The client graph's view of one catalog item, and the one display rule the
// three list views share.
//
// **Declared here rather than imported from the wire face**, and that is a hard
// boundary rather than a preference: the client graph — `components/`,
// `routes/`, `hooks/` — may never import `@/server/*`, type-only imports
// included (`eslint.config.js`, "The client-graph -> server-graph boundary").
// A type-only import encodes the layering backwards and is how the runtime ones
// start. `@/lib/background-jobs-format` stands in exactly this position for the
// jobs surface.
//
// The hole that opens — two hand-kept spellings of one shape — is closed by
// `@/api/contracts/catalog-wire-drift.unit.test.ts`, in the one tier that can
// see both sides. It fails to *compile* if the wire drops a field, renames one,
// or turns a required one optional.

/** A lookup row as the list renders it: something to show, and its id. */
export interface CatalogLookupRef {
  id: number;
  name: string;
}

/**
 * One row of the items list.
 *
 * A subset of the wire's item summary — the fields the three views actually
 * paint — rather than a mirror of it. Copying every wire field would make this
 * a second contract to maintain, and the drift pin only requires that what is
 * here is *assignable from* what the wire sends.
 *
 * `description` is here because the list view's third column is the donor's
 * (`item-data-row.tsx` draws it beside the fields, behind its own divider). It
 * is **sanitised HTML**, sanitised on the Python side — the only field on this
 * surface rendered as markup rather than text.
 */
export interface CatalogListItem extends CatalogItemBadgeInput {
  id: number;
  name: string;
  modelNumber?: string | null;
  jdeItemNumber?: string | null;
  /** Sanitised HTML — rendered as markup by the list view, nothing else. */
  description?: string | null;
  usageCount: number;
  manufacturer: CatalogLookupRef;
  category?: CatalogLookupRef | null;
  finish?: CatalogLookupRef | null;
  /**
   * The item's hero image, or absent/`null` when it has none.
   *
   * Server-resolved (#297): lowest `orderIndex`, tiebreak `id`, and the detail
   * response's `images` array is emitted hero-first so `images[0]` is the same
   * picture. Nothing on this side repeats the donor's
   * `hero_image -> is_hero -> images[0]` chain.
   *
   * `null`, never an empty-string URL — the illustration placeholder, its
   * pending-approval variant, and the fetch-error fallback all key on absence.
   */
  heroImage?: CatalogItemImageInput | null;
  /**
   * The variant group this item belongs to, or `null`/absent when it is
   * ungrouped.
   *
   * **This, not an empty member list, is what says whether an item is grouped**
   * — the wire's own instruction, and the difference between "this item has no
   * siblings" and "this page's response did not carry the group". The card looks
   * its tile strip up by it, because the groups ride beside the results rather
   * than on each row.
   *
   * Its value is the group leader's own item id (ADR-0005 decision 6), which
   * nothing on this side depends on.
   */
  variantGroupId?: number | null;
  /**
   * The item's max price, a fixed-scale decimal string (`"9.0200"`), or
   * `null`/absent when it has none — the Item Search card's chip (#1216).
   */
  maxPrice?: string | null;
}

/** What an empty field reads as, everywhere on this surface. */
export const CATALOG_FIELD_EMPTY = "N/A";

/**
 * A field's text, or `N/A`.
 *
 * Parity prints the placeholder rather than hiding the row, and that is a
 * scanning decision rather than a stylistic one: a card whose rows shift
 * position from item to item cannot be read down a column, which is the whole
 * way a grid of 20 cards gets used. An all-whitespace value counts as empty —
 * the source data has some, and a row of blank space with a label over it looks
 * like a rendering fault.
 */
export function fieldText(value: string | null | undefined): string {
  const trimmed = value?.trim();
  return trimmed ? trimmed : CATALOG_FIELD_EMPTY;
}
